import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWalletContext } from '../context/WalletContext.jsx';
import { CONFIG } from '../config';
import { PublicKey, Transaction } from '@solana/web3.js';
import { failoverConnection as rpc } from '../utils/failoverConnection';
import { createTransferInstruction, getAssociatedTokenAddress } from '@solana/spl-token';
import { logger } from '../utils/logger';

function SendWallet() {
  const { wallet, walletAddress } = useWalletContext();
  const navigate = useNavigate();
  // Session-only beneficiaries (no localStorage)
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [recipientAddress, setRecipientAddress] = useState('');
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState('');
  const [beneficiaryName, setBeneficiaryName] = useState('');

  // Load beneficiaries for this user from API
  useEffect(() => {
    const loadBeneficiaries = async () => {
      if (!walletAddress) return;
      try {
        const res = await fetch(`/api/wallet/beneficiaries?userWalletAddress=${encodeURIComponent(walletAddress)}`);
        if (!res.ok) throw new Error(`Failed to fetch beneficiaries (${res.status})`);
        const rows = await res.json();
        const normalized = Array.isArray(rows)
          ? rows.map(r => ({ address: r.beneficiary_wallet_address, name: r.name }))
          : [];
        setBeneficiaries(normalized);
      } catch (err) {
        logger.error('Load beneficiaries error:', err);
      }
    };
    loadBeneficiaries();
  }, [walletAddress]);

  const persistBeneficiaryLocally = (address, name) => {
    if (!address) return;
    const exists = beneficiaries.some((b) => b.address === address);
    const updated = exists
      ? beneficiaries.map((b) => (b.address === address ? { ...b, name: name || b.name } : b))
      : [...beneficiaries, { address, name }];
    setBeneficiaries(updated);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setStatus('Processing...');
    try {
      // Use Civic's useUser for solana.wallet
      if (!wallet) {
        setStatus('Wallet not connected or cannot send transactions.');
        return;
      }
      // Basic validations
      if (!recipientAddress) {
        setStatus('Please enter a recipient wallet address.');
        return;
      }
      if (!amount || Number(amount) <= 0) {
        setStatus('Please enter a valid amount.');
        return;
      }
      const connection = rpc;
      const USDC_MINT = new PublicKey('EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v');
      const fromATA = await getAssociatedTokenAddress(USDC_MINT, wallet.publicKey);
      const toATA = await getAssociatedTokenAddress(USDC_MINT, new PublicKey(recipientAddress));
      const amountInSmallestUnit = Math.round(Number(amount) * 1e6);
      const ix = createTransferInstruction(fromATA, toATA, wallet.publicKey, amountInSmallestUnit);
      const tx = new Transaction().add(ix);
      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction(signature, 'confirmed');
      setStatus(`Transaction sent! Tx ID: ${signature}`);

      // Record transfer in our database with exact amount provided
      try {
        await fetch('/api/wallet/transfer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userWalletAddress: wallet.publicKey.toBase58(),
            recipientWalletAddress: recipientAddress,
            amount: Number(amount),
            transactionHash: signature,
          }),
        });
      } catch (dbErr) {
        console.error('Failed to record transfer:', dbErr);
      }

      // Save beneficiary to DB only if a name was provided (optional)
      const trimmedName = beneficiaryName.trim();
      if (trimmedName.length > 0) {
        try {
          await fetch('/api/wallet/beneficiaries', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userWalletAddress: wallet.publicKey.toBase58(),
              beneficiaryWalletAddress: recipientAddress,
              name: trimmedName,
            }),
          });
          // Also persist locally for quick selection
          persistBeneficiaryLocally(recipientAddress, trimmedName);
        } catch (benErr) {
          console.error('Failed to save beneficiary:', benErr);
        }
      }

      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      logger.error(err);
      setStatus('Failed to send: ' + (err.message || err.toString()));
    }
  };

  return (
    <div className="sendwallet-bg">
      <div className="sendwallet-2col">
        {/* Left: Form */}
        <div className="sendwallet-formcol">
          <h1 className="sendwallet-title">Send to Wallet</h1>
          <form className="sendwallet-form" onSubmit={handleSend} autoComplete="off">
            <div className="sendwallet-label">Wallet Address</div>
            <input
              type="text"
              className="sendwallet-input"
              placeholder="Wallet Address"
              value={recipientAddress}
              onChange={e => setRecipientAddress(e.target.value)}
              autoComplete="off"
            />
            <div className="sendwallet-label" style={{marginTop:'2.2rem'}}>Amount</div>
            <input
              type="number"
              className="sendwallet-input"
              placeholder="Amount"
              min="0"
              step="0.01"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              required
            />
            <div className="sendwallet-label" style={{marginTop:'0.6rem'}}>Beneficiary Name (optional)</div>
            <input
              type="text"
              className="sendwallet-input"
              placeholder="e.g., Alice USDC Wallet"
              value={beneficiaryName}
              onChange={e => setBeneficiaryName(e.target.value)}
              autoComplete="off"
            />
            <button className="sendwallet-btn" type="submit">Process Payment</button>
            {status && <div className="success-message">{status}</div>}
          </form>
        </div>
        {/* Right: Beneficiaries */}
        <div className="sendwallet-bencol">
          <div className="sendwallet-benbox">
            <div className="sendwallet-bentitle">Beneficiaries</div>
            <div className="sendwallet-benlist">
              {beneficiaries.length > 0 ? (
                beneficiaries.map((item, index) => (
                  <div 
                    className="sendwallet-benitem" 
                    key={index}
                    onClick={() => setRecipientAddress(item.address || item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="sendwallet-benimg">
                      {(item.address || item).slice(0, 2).toUpperCase()}
                    </div>
                    <span className="sendwallet-benname">
                      {item.name || `${(item.address || item).slice(0, 6)}...${(item.address || item).slice(-4)}`}
                    </span>
                  </div>
                ))
              ) : (
                <div className="no-beneficiaries">
                  No saved beneficiaries. Add one using the form.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .sendwallet-bg {
          min-height: 100vh;
          background: #f7f9fa;
          padding-top: 32px;
        }
        .sendwallet-2col {
          display: flex;
          flex-direction: row;
          max-width: 1200px;
          margin: 0 auto;
          width: 100%;
          gap: 4vw;
        }
        .sendwallet-formcol {
          flex: 1.2;
          min-width: 340px;
          max-width: 520px;
          padding-top: 32px;
        }
        .sendwallet-title {
          font-size: 2rem;
          font-weight: 800;
          color: #181c23;
          margin-bottom: 2.2rem;
        }
        .sendwallet-label {
          font-size: 1.09rem;
          font-weight: 500;
          color: #232a36;
          margin-bottom: 0.7rem;
        }
        .sendwallet-input {
          width: 100%;
          padding: 1.1rem 1.2rem;
          border-radius: 14px;
          border: none;
          background: #eef2f7;
          font-size: 1.07rem;
          color: #232a36;
          margin-bottom: 0.5rem;
          margin-top: 0px;
          outline: none;
          font-weight: 500;
          box-sizing: border-box;
        }
        .sendwallet-input::placeholder {
          color: #82a0c6;
          opacity: 1;
          font-weight: 400;
        }
        .sendwallet-row {
          display: flex;
          flex-direction: row;
          align-items: center;
          margin-bottom: 2.2rem;
        }
        .sendwallet-btn {
          width: 100%;
          background: #2196f3;
          color: #fff;
          font-weight: 700;
          font-size: 1.09rem;
          border: none;
          border-radius: 999px;
          padding: 1.1rem 0;
          margin-top: 2.2rem;
          cursor: pointer;
          transition: background 0.2s;
        }
        .sendwallet-btn:hover {
          background: #1976d2;
        }
        .sendwallet-bencol {
          flex: 1;
          min-width: 270px;
          max-width: 350px;
          padding-top: 64px;
        }
        .sendwallet-benbox {
          background: transparent;
          padding: 0;
        }
        .sendwallet-bentitle {
          font-size: 1.25rem;
          font-weight: 700;
          margin-bottom: 1.5rem;
          color: #181c23;
        }
        .sendwallet-benlist {
          display: flex;
          flex-direction: column;
          gap: 1.1rem;
          margin-bottom: 2.2rem;
        }
        .sendwallet-benitem {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .sendwallet-benimg {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e3f2fd;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          color: #1976d2;
          font-weight: 600;
          font-size: 14px;
        }
        .no-beneficiaries {
          padding: 16px;
          color: #666;
          text-align: center;
          font-style: italic;
        }
        .sendwallet-benname {
          font-size: 1.07rem;
          color: #232a36;
          font-weight: 500;
        }
        .sendwallet-seeall {
          width: 100%;
          background: #eef2f7;
          color: #232a36;
          font-weight: 700;
          font-size: 1.01rem;
          border: none;
          border-radius: 999px;
          padding: 0.9rem 0;
          cursor: pointer;
          margin-top: 0.5rem;
        }
        @media (max-width: 900px) {
          .sendwallet-2col {
            flex-direction: column;
            gap: 0;
          }
          .sendwallet-formcol, .sendwallet-bencol {
            max-width: 100%;
            padding-top: 24px;
          }
          .sendwallet-bencol {
            padding-top: 32px;
          }
        }
      `}</style>
    </div>
  );
}

export default SendWallet;
